import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { LogOut } from "lucide-react";

import { useAuth } from "@/shared/contexts/AuthProvider";

const MENU_WIDTH = 224; // w-56

type Placement = "bottom-end" | "top-start";

interface MenuPosition {
  left: number;
  top?: number;
  bottom?: number;
}

export interface AccountMenuTriggerProps {
  open: boolean;
  toggle: () => void;
  ref: React.Ref<HTMLButtonElement>;
}

export interface AccountMenuProps {
  /** Where the menu opens relative to the trigger. */
  placement: Placement;
  /** Render the trigger button; wire the provided props onto it. */
  trigger: (props: AccountMenuTriggerProps) => ReactNode;
}

/**
 * Shared account dropdown (signed-in info + Log out). Portalled to <body> so it
 * is never clipped by the sidebar's `overflow:hidden` or the collapsed rail.
 * Used by both the topbar and the sidebar footer so they share one menu.
 */
export function AccountMenu({ placement, trigger }: AccountMenuProps) {
  const { displayName, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    if (placement === "bottom-end") {
      setPosition({
        top: rect.bottom + 8,
        left: Math.max(8, rect.right - MENU_WIDTH),
      });
    } else {
      // top-start: open upward, aligned to the trigger's left edge.
      setPosition({
        bottom: window.innerHeight - rect.top + 8,
        left: rect.left,
      });
    }
  }, [open, placement]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <>
      {trigger({ open, toggle: () => setOpen((v) => !v), ref: triggerRef })}
      {open && position
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={{
                position: "fixed",
                left: position.left,
                top: position.top,
                bottom: position.bottom,
                width: MENU_WIDTH,
              }}
              className="z-[100] rounded-xl border border-border-primary bg-background-card p-2 shadow-lg"
            >
              <div className="border-b border-border-primary px-3 py-2">
                <p className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
                  Signed in
                </p>
                <p className="mt-1 truncate text-sm font-medium text-text-primary">
                  {displayName ?? "Authenticated user"}
                </p>
              </div>
              <button
                type="button"
                role="menuitem"
                className="mt-2 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-text-primary transition-colors hover:bg-background-hover"
                onClick={() => {
                  setOpen(false);
                  void logout();
                }}
              >
                <LogOut className="h-4 w-4" />
                Log out
              </button>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
